import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Zap,
  Target,
  Users,
  TrendingUp,
  Award,
  Clock,
  MapPin,
  Mail,
  Phone,
  CheckCircle,
  Star,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import "../styles/tennisCoaching.css";
import groupTennis from "../assets/images/group-tennis.jpg";
import profilTennis from "../assets/images/face.jpg";
import Tournament from "../assets/images/smash.jpg";
import Balls from "../assets/images/balls.jpg";

function TennisCoaching() {
  const { t } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    level: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const levelOptions = t("tennis.booking.fields.levelOptions", {
    returnObjects: true,
  });

  const testimonials = t("tennis.testimonials.items", {
    returnObjects: true,
  });

  const philosophyCards = t("tennis.philosophy.cards", {
    returnObjects: true,
  });

  const privateBullets = t("tennis.programs.private.bullets", {
    returnObjects: true,
  });
  const groupBullets = t("tennis.programs.group.bullets", {
    returnObjects: true,
  });
  const tournamentBullets = t("tennis.programs.tournament.bullets", {
    returnObjects: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formPayload.append(key, value);
      });
      formPayload.append("formContext", "tennis");

      const response = await fetch("/contact.php", {
        method: "POST",
        body: formPayload,
      });

      let data;
      try {
        data = await response.json();
      } catch (err) {
        data = null;
      }

      if (!response.ok || !data || !data.success) {
        setErrorMessage(t("tennis.booking.errorMessage"));
        setIsSubmitted(false);
      } else {
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          level: "",
          message: "",
        });
      }
    } catch (err) {
      setErrorMessage(t("tennis.booking.errorMessage"));
      setIsSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tennis">
      {/* HERO */}
      <section className="tennis__hero">
        <div className="tennis__hero-bg">
          <img
            src={groupTennis}
            alt="Group tennis"
            className="tennis__hero-image"
          />
          <div className="tennis__hero-overlay" />
        </div>

        {/* balles animées */}
        <div className="tennis__hero-balls">
          <motion.div
            className="tennis__hero-ball tennis__hero-ball--big"
            animate={{
              y: [0, 30, 0],
              x: [0, 20, 0],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="tennis__hero-ball tennis__hero-ball--small"
            animate={{
              y: [0, -40, 0],
              x: [0, -15, 0],
              rotate: [360, 180, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="tennis__hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="tennis__hero-badge"
          >
            <Zap className="tennis__hero-badge-icon" />
            <span className="tennis__hero-badge-text">
              {t("tennis.hero.badge")}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="tennis__hero-title"
          >
            {t("tennis.hero.titleLine1")}
            <br />
            <span className="tennis__hero-title-highlight">
              {t("tennis.hero.titleHighlight")}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="tennis__hero-subtitle"
          >
            {t("tennis.hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <button
              type="button"
              className="tennis__hero-cta"
              onClick={() => {
                const el = document.getElementById("tennis-booking");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {t("tennis.hero.cta")}
            </button>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="tennis__section tennis__section--philosophy">
        <div className="tennis__philosophy-bg">
          <img
            src={profilTennis}
            alt="Profil tennis"
            className="tennis__philosophy-image"
          />
        </div>

        <div className="tennis__section-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="tennis__section-header"
          >
            <h2 className="tennis__section-title">
              {t("tennis.philosophy.sectionTitle")}
            </h2>
            <p className="tennis__section-subtitle">
              {t("tennis.philosophy.sectionSubtitle")}
            </p>
          </motion.div>

          <div className="tennis__cards-grid">
            {philosophyCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tennis__card"
              >
                <div className="tennis__card-icon-wrapper">
                  {index === 0 && <Target className="tennis__card-icon" />}
                  {index === 1 && <TrendingUp className="tennis__card-icon" />}
                  {index === 2 && <Award className="tennis__card-icon" />}
                </div>
                <h3 className="tennis__card-title">{card.title}</h3>
                <p className="tennis__card-text">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="tennis__section tennis__section--programs">
        <div className="tennis__court-pattern" />

        <div className="tennis__section-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="tennis__section-header"
          >
            <h2 className="tennis__section-title">
              {t("tennis.programs.sectionTitle")}
            </h2>
            <p className="tennis__section-subtitle">
              {t("tennis.programs.sectionSubtitle")}
            </p>
          </motion.div>

          <div className="tennis__programs-grid">
            {/* Private */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="tennis__program-card"
            >
              <div className="tennis__program-header">
                <div>
                  <h3 className="tennis__program-title">
                    {t("tennis.programs.private.title")}
                  </h3>
                  <p className="tennis__program-subtitle">
                    {t("tennis.programs.private.subtitle")}
                  </p>
                </div>
                <div className="tennis__program-icon">
                  <Target className="tennis__program-icon-svg" />
                </div>
              </div>
              <ul className="tennis__program-list">
                {privateBullets.map((item) => (
                  <li key={item} className="tennis__program-list-item">
                    <CheckCircle className="tennis__program-list-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="tennis__program-footer">
                <div className="tennis__program-price-row">
                  <span className="tennis__program-price">
                    {t("tennis.programs.private.price")}
                  </span>
                  <span className="tennis__program-price-unit">
                    {t("tennis.programs.private.priceUnit")}
                  </span>
                </div>
                <p className="tennis__program-note">
                  {t("tennis.programs.private.note")}
                </p>
              </div>
            </motion.div>

            {/* Group */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="tennis__program-card"
            >
              <div className="tennis__program-header">
                <div>
                  <h3 className="tennis__program-title">
                    {t("tennis.programs.group.title")}
                  </h3>
                  <p className="tennis__program-subtitle">
                    {t("tennis.programs.group.subtitle")}
                  </p>
                </div>
                <div className="tennis__program-icon">
                  <Users className="tennis__program-icon-svg" />
                </div>
              </div>
              <ul className="tennis__program-list">
                {groupBullets.map((item) => (
                  <li key={item} className="tennis__program-list-item">
                    <CheckCircle className="tennis__program-list-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="tennis__program-footer">
                <div className="tennis__program-price-row">
                  <span className="tennis__program-price">
                    {t("tennis.programs.group.price")}
                  </span>
                  <span className="tennis__program-price-unit">
                    {t("tennis.programs.group.priceUnit")}
                  </span>
                </div>
                <p className="tennis__program-note">
                  {t("tennis.programs.group.note")}
                </p>
              </div>
            </motion.div>

            {/* Tournament */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="tennis__tournament-card"
            >
              <div className="tennis__tournament-bg">
                <img
                  src={Tournament}
                  alt="Tournament"
                  className="tennis__tournament-image"
                />
                <div className="tennis__tournament-overlay" />
              </div>

              <div className="tennis__tournament-content">
                <div className="tennis__tournament-left">
                  <div className="tennis__tournament-header">
                    <Award className="tennis__tournament-icon" />
                    <h3 className="tennis__tournament-title">
                      {t("tennis.programs.tournament.title")}
                    </h3>
                  </div>
                  <p className="tennis__tournament-subtitle">
                    {t("tennis.programs.tournament.subtitle")}
                  </p>
                  <div className="tennis__tournament-list">
                    {tournamentBullets.map((item) => (
                      <div key={item} className="tennis__tournament-list-item">
                        <CheckCircle className="tennis__tournament-list-icon" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="tennis__tournament-box">
                  <div className="tennis__tournament-badge">
                    {t("tennis.programs.tournament.badge")}
                  </div>
                  <div className="tennis__tournament-box-title">
                    {t("tennis.programs.tournament.boxTitle")}
                  </div>
                  <p className="tennis__tournament-box-subtitle">
                    {t("tennis.programs.tournament.boxSubtitle")}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="tennis__section tennis__section--testimonials">
        <div className="tennis__testimonials-bg">
          <img
            src={Balls}
            alt="balls_testimonials"
            className="tennis__testimonials-image"
          />
        </div>

        <div className="tennis__section-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="tennis__section-header"
          >
            <h2 className="tennis__section-title">
              {t("tennis.testimonials.sectionTitle")}
            </h2>
          </motion.div>

          <div className="tennis__testimonials-grid">
            {testimonials.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tennis__testimonial-card"
              >
                <div className="tennis__testimonial-stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="tennis__testimonial-star" />
                  ))}
                </div>
                <p className="tennis__testimonial-text">“{item.text}”</p>
                <div className="tennis__testimonial-author">
                  <p className="tennis__testimonial-name">{item.name}</p>
                  <p className="tennis__testimonial-role">{item.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section
        id="tennis-booking"
        className="tennis__section tennis__section--booking"
      >
        <div className="tennis__section-inner tennis__section-inner--booking">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="tennis__booking-card"
          >
            <div className="tennis__booking-hero">
              <div className="tennis__booking-hero-bg">
                <div className="tennis__booking-overlay" />
              </div>
              <div className="tennis__booking-hero-content">
                <h2 className="tennis__booking-title">
                  {t("tennis.booking.sectionTitle")}
                </h2>
                <p className="tennis__booking-subtitle">
                  {t("tennis.booking.sectionSubtitle")}
                </p>
              </div>
            </div>

            <div className="tennis__booking-body">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="tennis__booking-success"
                >
                  <div className="tennis__booking-success-icon-wrapper">
                    <CheckCircle className="tennis__booking-success-icon" />
                  </div>
                  <h3 className="tennis__booking-success-title">
                    {t("tennis.booking.successTitle")}
                  </h3>
                  <p className="tennis__booking-success-text">
                    {t("tennis.booking.successText")}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="tennis__form">
                  <div className="tennis__form-row">
                    <div className="tennis__form-field">
                      <label className="tennis__form-label">
                        {t("tennis.booking.fields.nameLabel")}
                      </label>
                      <input
                        type="text"
                        required
                        className="tennis__input"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder={t("tennis.booking.fields.namePlaceholder")}
                      />
                    </div>
                    <div className="tennis__form-field">
                      <label className="tennis__form-label">
                        {t("tennis.booking.fields.emailLabel")}
                      </label>
                      <input
                        type="email"
                        required
                        className="tennis__input"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "tennis.booking.fields.emailPlaceholder"
                        )}
                      />
                    </div>
                  </div>

                  <div className="tennis__form-row">
                    <div className="tennis__form-field">
                      <label className="tennis__form-label">
                        {t("tennis.booking.fields.phoneLabel")}
                      </label>
                      <input
                        type="text"
                        className="tennis__input"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            phone: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "tennis.booking.fields.phonePlaceholder"
                        )}
                      />
                    </div>
                    <div className="tennis__form-field">
                      <label className="tennis__form-label">
                        {t("tennis.booking.fields.levelLabel")}
                      </label>
                      <select
                        className="tennis__select"
                        value={formData.level}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            level: e.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {t("tennis.booking.fields.levelPlaceholder")}
                        </option>
                        <option value="beginner">
                          {levelOptions.beginner}
                        </option>
                        <option value="intermediate">
                          {levelOptions.intermediate}
                        </option>
                        <option value="advanced">
                          {levelOptions.advanced}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="tennis__form-field">
                    <label className="tennis__form-label">
                      {t("tennis.booking.fields.messageLabel")}
                    </label>
                    <textarea
                      required
                      className="tennis__textarea"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      placeholder={t(
                        "tennis.booking.fields.messagePlaceholder"
                      )}
                    />
                  </div>

                  {errorMessage && (
                    <p className="tennis__form-error">
                      {errorMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="tennis__submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? t("tennis.booking.submittingLabel")
                      : t("tennis.booking.submitLabel")}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* CONTACT INFO */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="tennis__contact-grid"
          >
            <div className="tennis__contact-item">
              <div className="tennis__contact-icon-wrapper">
                <MapPin className="tennis__contact-icon" />
              </div>
              <div>
                <p className="tennis__contact-label">
                  {t("tennis.booking.contact.locationLabel")}
                </p>
                <p className="tennis__contact-value">
                  {t("tennis.booking.contact.locationValue")}
                </p>
              </div>
            </div>

            {/* <div className="tennis__contact-item">
              <div className="tennis__contact-icon-wrapper">
                <Phone className="tennis__contact-icon" />
              </div>
              <div>
                <p className="tennis__contact-label">
                  {t("tennis.booking.contact.phoneLabel")}
                </p>
                <p className="tennis__contact-value">
                  {t("tennis.booking.contact.phoneValue")}
                </p>
              </div>
            </div> */}
            <div className="tennis__contact-item">
              <div className="tennis__contact-icon-wrapper">
                <Mail className="tennis__contact-icon" />
              </div>
              <div>
                <p className="tennis__contact-label">
                  {t("tennis.booking.contact.emailLabel")}
                </p>
                <p className="tennis__contact-value">
                  {t("tennis.booking.contact.emailValue")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default TennisCoaching;
